import { createRoutes, param, str } from '../typesafe-routes';
import {
  getCurrentGyeonggiTenantSlug,
  isGyeonggiTenantReservedRouteSegment,
} from './gyeonggi-do-tenant-path';

/**
 * basePath 의 tenant slug. `/login`·`/courses` 같은 예약 세그먼트는 slug 가 아니므로
 * parse 에서 throw 해 matcher 가 이 자리를 건너뛰고 다음 세그먼트부터 맞추게 한다.
 * 생략하면 현재 브라우저 경로의 slug(없으면 기본 slug)로 렌더된다.
 */
const tenantSlug = param<string>({
  parse: (value) => {
    if (isGyeonggiTenantReservedRouteSegment(value)) {
      throw new Error(`"${value}" is a reserved route segment`);
    }
    return value;
  },
  serialize: (value) => value,
});

export const gyeonggiDoSchoolRoutes = createRoutes(
  {
    home: { path: [] },
    introduction: { path: ['introduction'] },
    signup: {
      path: ['signup'],
      children: {
        ageSelect: { path: ['age-select'], query: [str.optional('sessionId')] },
        consent: { path: ['consent'], query: [str.optional('sessionId')] },
        account: { path: ['account'], query: [str.optional('sessionId')] },
        info: { path: ['info'], query: [str.optional('sessionId')] },
        kids: { path: ['kids'], query: [str.optional('sessionId')] },
        instructor: { path: ['instructor'], query: [str.optional('sessionId')] },
        consultant: { path: ['consultant'] },
      },
    },
    consent: { path: ['consent'] },
    login: {
      path: ['login'],
      query: [str.optional('mode'), str.optional('returnTo')],
      children: {
        mfa: { path: ['mfa'] },
      },
    },
    courses: {
      path: ['courses'],
      children: {
        type: { path: ['type', str('type')] },
        create: { path: ['create'] },
        byId: {
          path: [str('id')],
          children: {
            edit: {
              path: ['edit'],
              children: {
                live: { path: ['live'], children: { info: { path: ['info'] } } },
                content: { path: ['content'], children: { info: { path: ['info'] } } },
              },
            },
            reviews: {
              path: ['reviews'],
              children: {
                write: { path: ['write'] },
                byReviewId: {
                  path: [str('reviewId')],
                  children: { edit: { path: ['edit'] } },
                },
              },
            },
          },
        },
      },
    },
    instructor: {
      path: ['instructor'],
      children: {
        courses: {
          path: ['courses'],
          children: {
            type: { path: ['type', str('type')] },
            create: { path: ['create'] },
            byId: {
              path: [str('id')],
              children: {
                dashboard: { path: ['dashboard'] },
                classSession: { path: ['class-session'] },
                attendance: { path: ['attendance'] },
                students: { path: ['students'] },
                communication: {
                  path: ['communication'],
                  query: [str.optional('board'), str.optional('list')],
                  children: {
                    byBoardId: {
                      path: [str('boardId')],
                      children: {
                        write: { path: ['write'] },
                        byPostId: {
                          path: [str('postId')],
                          children: {
                            edit: { path: ['edit'] },
                          },
                        },
                      },
                    },
                  },
                },
                surveys: {
                  path: ['surveys'],
                  children: {
                    bySurveyId: { path: [str('surveyId')] },
                  },
                },
                reviews: { path: ['reviews'] },
                courseInstructors: { path: ['instructors'] },
                subtitles: { path: ['subtitles'] },
                settings: { path: ['settings'] },
                edit: {
                  path: ['edit'],
                  children: {
                    live: { path: ['live'], children: { info: { path: ['info'] } } },
                    content: { path: ['content'], children: { info: { path: ['info'] } } },
                  },
                },
              },
            },
          },
        },
        cms: {
          path: ['cms'],
          children: {
            metaContent: { path: ['meta-content'] },
            curriculum: { path: ['curriculum'] },
          },
        },
        myInfo: {
          path: ['my-info'],
          children: {
            account: { path: ['account'] },
            instructorProfile: { path: ['instructor-profile'] },
          },
        },
        counseling: {
          path: ['counseling'],
          children: {
            byId: {
              path: [str('id')],
              children: {
                // 상담 배정은 byId 인덱스(추가 세그먼트 없음)
                sessions: { path: ['sessions'] }, // 내 상담
                students: { path: ['students'] }, // 수강생 관리
              },
            },
          },
        },
      },
    },
    myLearning: {
      path: ['my-learning'],
      children: {
        reviews: { path: ['reviews'] },
        byId: {
          path: [str('id')],
          children: {
            lessons: { path: ['lessons'] },
            info: { path: ['info'] },
            activityBoard: { path: ['activity-board'] },
            boards: {
              path: ['boards'],
              children: {
                byBoardId: {
                  path: [str('boardId')],
                  children: {
                    write: { path: ['write'] },
                  },
                },
              },
            },
          },
        },
        live: { path: ['live'] },
      },
    },
    myCounseling: {
      path: ['my-counseling'],
      children: {
        // 내 정보 수정 (제출한 신청의 진로/기타 정보 수정 — 동적 폼)
        edit: { path: ['edit'] },
      },
    },
    // 상담 신청 플로우. 내 상담실(`my-counseling`)과는 별개의 형제 진입점이라
    // 하위 라우트가 아닌 top-level로 둔다(GNB 메뉴도 서로 형제, active 충돌 방지).
    counselingApply: {
      path: ['counseling-apply'],
      children: {
        // Step 2 (신청폼)
        form: { path: ['form'] },
        // Step 3 (휴대전화 입력/인증)
        phone: { path: ['phone'] },
        // Step 4 (신청 완료)
        complete: { path: ['complete'] },
      },
    },
    student: {
      path: ['s'],
      children: {
        group: {
          path: ['group', str('groupId')],
          children: {
            regularClass: {
              path: ['regular-class', str('smiId')],
              children: {
                learning: {
                  path: ['learning', str('learningActivityId')],
                  query: [str.optional('courseId'), str.optional('chatbot')],
                },
                summary: {
                  path: ['summary', str('suiId')],
                  query: [str.optional('courseId')],
                },
              },
            },
          },
        },
      },
    },
    surveys: {
      path: ['surveys'],
      children: {
        byId: {
          path: [str('id')],
        },
      },
    },
    posts: {
      path: ['posts'],
      children: {
        byPostId: {
          path: [str('postId')],
          query: [str.optional('returnTo')],
          children: {
            edit: { path: ['edit'], query: [str.optional('returnTo')] },
          },
        },
      },
    },
    account: {
      path: ['account'],
      children: {
        password: {
          path: ['password'],
          children: {
            reset: { path: ['reset'] },
          },
        },
        find: { path: ['find'] },
      },
    },
    admin: {
      path: ['admin'],
      children: {
        users: { path: ['users'] },
        contents: { path: ['contents'] },
      },
    },
    /** (sideLayout)/cms — 학습 자료 관리 */
    cms: {
      path: ['cms'],
      children: {
        metaContent: { path: ['meta-content'] },
        curriculum: { path: ['curriculum'] },
      },
    },
    tenant: {
      path: ['tenant'],
      children: {
        tenants: { path: ['tenants'] },
        menu: { path: ['menu'] },
        contentConnection: { path: ['content-connection'] },
        instructors: { path: ['instructors'] },
        security: { path: ['security'] },
        permission: { path: ['permission'] },
        settings: { path: ['settings'] },
      },
    },
    myInfo: {
      path: ['my-info'],
      children: {
        account: { path: ['account'] },
      },
    },
    user: {
      path: ['user'],
      children: {
        messages: { path: ['messages'] },
      },
    },
    boards: {
      path: ['boards'],
      children: {
        bySlug: {
          path: [str('slug')],
          children: {
            write: { path: ['write'] },
          },
        },
      },
    },
    pages: {
      path: ['pages'],
      children: {
        bySlug: { path: [str('slug')] },
      },
    },
    search: { path: ['search'], query: [str.optional('q')] },
    te: {
      path: ['te'],
      children: {
        dashboard: { path: ['dashboard'] },
        memberships: { path: ['memberships'] },
        securityLogs: { path: ['security-logs'] },
        instructorApplications: { path: ['instructor-applications'] },
        courseApplications: { path: ['course-applications'] },
        counseling: {
          path: ['counseling'],
          children: {
            create: { path: ['create'] },
            byId: {
              path: [str('id')],
              children: {
                applications: { path: ['applications'] },
                progress: { path: ['progress'] },
                info: { path: ['info'] },
                policy: { path: ['policy'] },
              },
            },
          },
        },
        features: { path: ['features'] },
        settings: { path: ['settings'] },
        branding: { path: ['branding'] },
        relatedSites: { path: ['related-sites'] },
        homeCuration: { path: ['home-curation'] },
        homePopupManagement: { path: ['home-popup-management'] },
        gnbManagement: { path: ['gnb-management'] },
        sharedDrive: { path: ['shared-drive'] },
        courseListCuration: { path: ['course-list-curation'] },
        reviews: { path: ['reviews'] },
        customContent: {
          path: ['custom-content'],
          children: {
            write: { path: ['write'] },
            byId: {
              path: [str('id')],
              children: {
                edit: { path: ['edit'] },
              },
            },
          },
        },
        courses: {
          path: ['courses'],
          children: {
            byId: {
              path: [str('id')],
              children: {
                communication: {
                  path: ['communication'],
                  query: [str.optional('board'), str.optional('list')],
                  children: {
                    byBoardId: {
                      path: [str('boardId')],
                      children: {
                        write: { path: ['write'] },
                        byPostId: {
                          path: [str('postId')],
                          children: {
                            edit: { path: ['edit'] },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
        createBoard: { path: ['create-board'] },
        boards: {
          path: ['boards'],
          children: {
            bySlug: {
              path: [str('slug')],
              children: {
                settings: { path: ['settings'] },
                write: { path: ['write'] },
                byPostId: {
                  path: [str('postId')],
                  children: { edit: { path: ['edit'] } },
                },
              },
            },
          },
        },
        surveys: {
          path: ['surveys'],
          children: {
            byId: {
              path: [str('id')],
            },
          },
        },
      },
    },
  },
  { basePath: [tenantSlug.optional('tenantSlug', { defaultValue: () => getCurrentGyeonggiTenantSlug() })] },
);
